// Port of src/app/app.component.{ts,html,scss} (mypart-app-root) — the app
// shell, rendered as the root layout route (see app/router.tsx).
//
// Lifecycle mapping:
// - constructor (theme init, non-www redirect, subdomain environment rewrite,
//   white-label URL params) → runAppConstructor(), executed once during the
//   first render, BEFORE any child renders/mount-effects — exactly when the
//   Angular constructor ran relative to child components. Angular DI also
//   constructed MobileDeviceService/UserStateService before any component
//   initialized, so initDeviceStore()/initUserStateTimers() run here too.
// - ngOnInit (showEnterprise console art + initMobileListeners) → mount
//   effect. Angular never removed the mobile window listeners — same here.
// - ngAfterViewInit:
//   - initFlowbite(): NOT ported — no React component uses Flowbite's JS
//     (the ported modals/overlays drive visibility themselves).
//   - MutationObserver on globalState.navBar → marginTop: effect keyed on the
//     store's navBar element (set by NavBar's mount effect).
//   - the 3s-delayed scroll subscription on #results-scroll-container: only
//     the mobile-scroll half (userScroll → handleScrollingOnMobile) lives
//     here; the infinite-scroll half (loadMore at 50% scroll height) was
//     ported into ResultsGrid (which owns infinite scroll) — see
//     features/results/ResultsGrid.tsx. Same container, same throttleTime(100)
//     leading-edge semantics.
// - ngOnDestroy (affiliationTimerService.stopTimer()) → mount-effect cleanup.
// - <ngx-loading-bar [includeSpinner]="false" color="#C32727"> →
//   react-top-loading-bar driven by the loader store's isLoading (the Angular
//   bar was driven by the HTTP interceptor; the React HTTP layer drives the
//   loader store the same way).
// - Template (app.component.html) → the JSX below, Tailwind classes verbatim.
//   The router outlet is react-router's <Outlet/>; NavBar / Playlist drawer /
//   TopPlaylists overlay / AdminPanel / Profile / ModalHost / Toasts stay
//   outside route switching, exactly like Angular's app.component.html.
// - setNavigateFn registration wires app/navigation.ts (ported
//   `router.navigate` call sites) to the real react-router navigate.
// - NavBar gets routeKey={location.key} — the Router NavigationEnd
//   re-subscription (see NavBar's handleNavigationEnd).

import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import LoadingBar, { type LoadingBarRef } from 'react-top-loading-bar';
import NavBar from './features/nav-bar/NavBar';
import Playlist from './features/playlist/Playlist';
import TopPlaylists from './features/top-playlists/TopPlaylists';
import ModalHost from './shared/modals/ModalHost';
import Toasts from './shared/components/Toasts';
import AppLoader from './shared/components/AppLoader';
import ErrorBoundary from './shared/components/ErrorBoundary';

// Code-split the heavy app-shell overlays most users never open on first paint.
// NOTE(port): Angular eagerly imported these (Profile lived in AppModule, admin
// alongside it). React.lazy splits each into its own chunk — loaded only when
// the user opens the panel — which Angular's eager module graph never did here.
const AdminPanel = lazy(() => import('./features/admin/AdminPanel'));
const Profile = lazy(() => import('./features/profile/Profile'));
import { useGlobalStore } from './stores/global-store';
import { useView, getView } from './features/view/use-view';
import { useLoaderStore } from './stores/loader-store';
import { initDeviceStore } from './stores/device-store';
import { initUserStateTimers } from './stores/user-store';
import { stopAffiliationTimer } from './api/affiliation-timer';
import { whiteLabelUser } from './api/white-label-user';
import { environment } from './config/environment';
import { setNavigateFn } from './app/navigation';
import './App.scss';

function getQueryParam(param: string): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(param);
}

function getSubdomain(): string | null {
  const host = window.location.host;
  if (host.startsWith('www') || host.startsWith('localhost:')) {
    return null; // No need to change anything if it's "www"
  }
  return host.split('.')[0];
}

function updateEnvironmentUrlsIfNeeded(subdomain: string | null): void {
  if (subdomain) {
    environment.baseUrl = environment.baseUrl.replace(/^(https?:\/\/)([^\/]+)/, `$1${subdomain}.mypart.com`);
    environment.baseUrlWithApi = environment.baseUrlWithApi.replace(/^(https?:\/\/)([^\/]+)/, `$1${subdomain}.mypart.com`);
    environment.baseUrlWithApiSh = environment.baseUrlWithApiSh.replace(/^(https?:\/\/)([^\/]+)/, `$1${subdomain}.mypart.com`);
  }
}

function showEnterprise() {
  console.log(`
 ______
                                ___.--------'------'---------.____
                          _.---'----------------------------------'---.__
                        .'___=]===========================================
,-----------------------..__/.'         >--.______        _______.---'
]====================<==||(__)        .'          '------'
'-----------------------' ----.___--/
     /       /---'                 '/'
    /_______(___USS_SONGHUNT_____/
    '-------------.--------------.'
                   \\________|_.-'
`);
}

// --- AppComponent constructor (run-once, render-phase: must complete before
// any child component renders — MainContent's init reads whiteLabelUser and
// the API modules read the possibly-rewritten environment URLs) ---
let appConstructorRan = false;

function runAppConstructor(): void {
  if (appConstructorRan) {
    return;
  }
  appConstructorRan = true;

  const currentUrl = window.location.href;

  useGlobalStore.getState().initAppTheme();

  // Redirect non-www version (as previously discussed)
  if (currentUrl.startsWith('https://mypart.com')) {
    window.location.href = currentUrl.replace('https://mypart.com', 'https://www.mypart.com');
  }

  const subdomain = getSubdomain();
  if (subdomain) {
    updateEnvironmentUrlsIfNeeded(subdomain);
  }

  if (window.location.href.includes('isWhiteLabelUser')) {
    const queryEmail = getQueryParam('em');
    const queryPass = getQueryParam('ps');

    const email = decodeURIComponent(queryEmail!);
    const pass = decodeURIComponent(queryPass!);

    whiteLabelUser.whiteLabelEmail = email;
    whiteLabelUser.whiteLabelPass = pass;
  }

  // Angular DI constructed these root services before any component existed:
  // MobileDeviceService's BreakpointObserver subscription → initDeviceStore();
  // UserStateService's constructor sign-up-popup timer → initUserStateTimers().
  initDeviceStore();
  initUserStateTimers();
}

export default function App() {
  runAppConstructor();

  const navigateImpl = useNavigate();
  const location = useLocation();

  const view = useView();
  const showPlaylist = useGlobalStore(s => s.showPlaylist);
  const showAdminPanel = useGlobalStore(s => s.showAdminPanel);
  const showProfilePage = useGlobalStore(s => s.showProfilePage);
  const navBar = useGlobalStore(s => s.navBar);
  const isLoading = useLoaderStore(s => s.isLoading);

  const [marginTop, setMarginTop] = useState(0);
  const loadingBarRef = useRef<LoadingBarRef>(null);
  const userScrollRef = useRef(false);

  // Keep the playlist drawer mounted for its 0.5s slide-out (Angular's
  // 'slideInOut' @trigger :leave). When showPlaylist flips to false we render a
  // leaving drawer for the animation duration, then unmount it.
  const [drawerVisible, setDrawerVisible] = useState(showPlaylist);
  const [drawerLeaving, setDrawerLeaving] = useState(false);
  useEffect(() => {
    if (showPlaylist) {
      setDrawerLeaving(false);
      setDrawerVisible(true);
      return;
    }
    if (drawerVisible) {
      setDrawerLeaving(true);
      const t = window.setTimeout(() => {
        setDrawerVisible(false);
        setDrawerLeaving(false);
      }, 500);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPlaylist]);

  // Register the real navigate with app/navigation.ts (ported router.navigate
  // call sites: navigate('/', { search }) etc.).
  useEffect(() => {
    setNavigateFn((path, opts) => {
      const search = opts?.search
        ? (opts.search.startsWith('?') ? opts.search : `?${opts.search}`)
        : '';
      navigateImpl({ pathname: path, search }, { replace: opts?.replace });
    });
  }, [navigateImpl]);

  // <ngx-loading-bar> ran indeterminately while requests were in flight.
  useEffect(() => {
    if (isLoading) {
      loadingBarRef.current?.continuousStart();
    } else {
      loadingBarRef.current?.complete();
    }
  }, [isLoading]);

  // ngOnInit: showEnterprise() + initMobileListeners(). The window listeners
  // were never removed in Angular — they live for the app lifetime here too.
  // ngOnDestroy: affiliationTimerService.stopTimer() → cleanup.
  useEffect(() => {
    showEnterprise();

    if (window.innerWidth < 640) {
      // Mouse down event - common for all devices including iOS
      window.addEventListener('mousedown', () => userScrollRef.current = true);

      // Key down event - for keyboard interactions (less relevant for iOS unless an external keyboard is used)
      window.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
          userScrollRef.current = true;
        }
      });

      // Touch start event - common for touch devices including iOS
      window.addEventListener('touchstart', () => userScrollRef.current = true);

      // Consider adding 'touchmove' for more continuous tracking on iOS
      window.addEventListener('touchmove', () => userScrollRef.current = true);
    }

    return () => stopAffiliationTimer();
  }, []);

  // ngAfterViewInit: initMutationObserver() — observe the nav bar element
  // (published to the global store by NavBar's mount effect) and recompute the
  // content padding from its height on every mutation + once initially.
  useEffect(() => {
    if (!navBar) {
      return;
    }

    const logNavBarHeight = () => {
      if (getView() === 'home') {
        setMarginTop(0);
      } else {
        setMarginTop(navBar.offsetHeight);
      }
    };

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' || mutation.type === 'childList' || mutation.type === 'characterData') {
          logNavBarHeight();
        }
      });
    });

    mutationObserver.observe(navBar, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true
    });

    // Log the initial height
    logNavBarHeight();

    return () => mutationObserver.disconnect();
  }, [navBar]);

  // ngAfterViewInit: the 3s-delayed scroll subscription on
  // #results-scroll-container — mobile-scroll half only (the loadMore half is
  // owned by ResultsGrid; see file header). throttleTime(100) leading-edge.
  useEffect(() => {
    let scrollContainer: HTMLElement | null = null;
    let throttled = false;
    let throttleTimer: ReturnType<typeof setTimeout> | undefined;

    const handleScrollingOnMobile = () => {
      const globalStore = useGlobalStore.getState();
      const filterWasClicked = globalStore.filterWasClickedDontHideFilters;
      // Check for scroll down and screen width less than 640px
      if (window.innerWidth < 640 && !filterWasClicked) {
        globalStore.setGlobalField('scrollEventOnMobile', true);
      }
      globalStore.setGlobalField('filterWasClickedDontHideFilters', false);
    };

    const onScroll = () => {
      if (throttled) {
        return;
      }
      throttled = true;
      throttleTimer = setTimeout(() => {
        throttled = false;
      }, 100);

      if (userScrollRef.current) {
        handleScrollingOnMobile();
      }
      userScrollRef.current = false; // Reset the flag after handling the scroll event
    };

    const startupTimer = setTimeout(() => {
      // Get the scroll container element
      scrollContainer = document.getElementById('results-scroll-container');
      scrollContainer?.addEventListener('scroll', onScroll);
    }, 3000);

    return () => {
      clearTimeout(startupTimer);
      clearTimeout(throttleTimer);
      scrollContainer?.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <>
      <LoadingBar ref={loadingBarRef} color="#C32727" shadow={false} />
      <NavBar routeKey={location.key} />
      <div style={{ paddingTop: `${marginTop}px` }} className="main-theme pb-4">
        {/* Route content boundary: a crash in the routed page degrades to a
            retry prompt while the NavBar/shell (outside the Outlet) stays up. */}
        <ErrorBoundary
            name="route"
            fallback={(retry) => (
              <div className="flex flex-col items-center justify-center gap-4 p-12 text-center text-gray-700 dark:text-gray-300">
                <p className="text-lg font-medium">Something went wrong loading this page.</p>
                <button
                    onClick={retry}
                    className="px-4 py-2 rounded-lg bg-mypart_red-500 text-white hover:bg-mypart_red-600 transition-colors">
                  Try again
                </button>
              </div>
            )}>
          <Outlet />
        </ErrorBoundary>
      </div>

      {drawerVisible && (
        <>
          <div className={`${drawerLeaving ? 'app-playlist-drawer-leave' : 'app-playlist-drawer-enter'} fixed right-0 top-0 h-full w-full sm:w-96 z-50 transition-all rounded-lg shadow-2xl light-gradient dark:dark-gradient`}>
            <Playlist />
          </div>
          <div onClick={() => useGlobalStore.getState().setGlobalField('showPlaylist', false)}
               className={`${drawerLeaving ? 'app-playlist-backdrop-leave' : ''} bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-40`}></div>
        </>
      )}
      {view === 'curated' && (
        <>
          <TopPlaylists />
          {/* NOTE(port): stray "pa" text node rendered verbatim from
              app.component.html line 15 (`...</mypart-top-playlists>pa`). */}
          pa
        </>
      )}
      {showAdminPanel && (
        <ErrorBoundary name="admin-panel" fallback={null}>
          <Suspense fallback={null}>
            <AdminPanel />
          </Suspense>
        </ErrorBoundary>
      )}

      {showProfilePage && (
        <ErrorBoundary name="profile" fallback={null}>
          <Suspense fallback={null}>
            <Profile />
          </Suspense>
        </ErrorBoundary>
      )}

      <ModalHost />

      <Toasts />

      <AppLoader />
    </>
  );
}
